import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

// ── PALETTE ──────────────────────────────────────────────────
const C = {
  bg: '#030b16', bgMid: '#060f1e', bgCard: '#091729', bgPanel: '#071323',
  bgRow: '#0a1727', bgRowAlt: '#0c1c30',
  cyan: '#00b8cc', cyanDim: 'rgba(0,184,204,0.12)', cyanBorder: 'rgba(0,184,204,0.22)', cyanSoft: 'rgba(0,184,204,0.06)',
  orange: '#e85d26',
  white: '#f0f4f8', gray: '#7a8799', grayLt: '#b0bcc8',
  border: 'rgba(255,255,255,0.07)', borderMid: 'rgba(255,255,255,0.04)',
  green: '#22c55e',
  amber: '#f59e0b',
  red: '#ef4444',
  lavender: '#a78bfa',
  purple: '#8b5cf6', purpleDim: 'rgba(139,92,246,0.14)', purpleBorder: 'rgba(139,92,246,0.30)',
};

const API_URL = process.env.REACT_APP_API_URL || '';

// ── ANIMATIONS CSS ───────────────────────────────────────────
const ANIMATIONS_CSS = `
  @keyframes lb-pulse        { 0%,100%{opacity:1} 50%{opacity:.35} }
  @keyframes lb-pulseStrong  { 0%,100%{opacity:1} 50%{opacity:.55} }
  @keyframes lb-fadeIn       { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
  @keyframes lb-overdueRing  {
    0%   { box-shadow: 0 0 0 0 rgba(239,68,68,0.55); }
    70%  { box-shadow: 0 0 0 6px rgba(239,68,68,0.00); }
    100% { box-shadow: 0 0 0 0 rgba(239,68,68,0.00); }
  }
  @keyframes lb-flow {
    0% { stroke-dashoffset: 16; }
    100% { stroke-dashoffset: 0; }
  }
  @keyframes lb-scanline { 0%{transform:translateY(-100%)} 100%{transform:translateY(100vh)} }

  /* Tabela */
  .lab-table { width:100%; border-collapse: separate; border-spacing: 0; }
  .lab-table thead th {
    position: sticky; top: 0; z-index: 2;
    background: linear-gradient(180deg, #0a1830 0%, #081428 100%);
    border-bottom: 1px solid rgba(0,184,204,0.22);
    padding: 11px 8px;
    font: 600 10px/1.2 'JetBrains Mono', monospace;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #8aa0b8;
    text-align: left;
    white-space: nowrap;
  }
  .lab-table tbody tr { transition: background 0.12s; }
  .lab-table tbody tr:nth-child(even) { background: rgba(255,255,255,0.012); }
  .lab-table tbody tr:hover { background: rgba(0,184,204,0.045); }
  .lab-table tbody td {
    padding: 11px 8px;
    border-bottom: 1px solid rgba(255,255,255,0.04);
    vertical-align: middle;
    font-size: 12px;
  }

  .lab-scroll::-webkit-scrollbar { width: 10px; height: 10px; }
  .lab-scroll::-webkit-scrollbar-track { background: rgba(255,255,255,0.02); border-radius: 6px; }
  .lab-scroll::-webkit-scrollbar-thumb { background: rgba(0,184,204,0.3); border-radius: 6px; border: 2px solid #030b16; }
  .lab-scroll::-webkit-scrollbar-thumb:hover { background: rgba(0,184,204,0.5); }

  .lab-input {
    height: 36px;
    padding: 0 12px;
    border-radius: 7px;
    border: 1px solid rgba(255,255,255,0.08);
    background: rgba(255,255,255,0.025);
    color: #f0f4f8;
    font: 500 12.5px/1 'Inter', sans-serif;
    outline: none;
    transition: border-color 0.15s, background 0.15s;
    appearance: none;
    cursor: pointer;
  }
  .lab-input:focus, .lab-input:hover { border-color: rgba(0,184,204,0.4); background: rgba(0,184,204,0.04); }
  .lab-input::-webkit-calendar-picker-indicator { filter: invert(0.7); cursor: pointer; }
  /* Conserta texto das options nos dropdowns (eram invisíveis com fundo escuro) */
  .lab-input option {
    background: #071323 !important;
    color: #f0f4f8 !important;
    font-family: 'Inter', sans-serif;
  }
  .lab-input option:checked, .lab-input option:hover {
    background: #0a1828 !important;
  }
`;

// ── HELPERS ──────────────────────────────────────────────────
const fmtDataBR = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
  } catch { return '—'; }
};

function urgency(d: number | null | undefined) {
  if (d === null || d === undefined) return { band: 'ok', color: C.gray, bg: 'transparent', bold: false };
  if (d <= 3) return { band: 'ok', color: C.gray, bg: 'transparent', bold: false };
  if (d <= 7) return { band: 'warn', color: C.amber, bg: `${C.amber}10`, bold: false };
  if (d <= 14) return { band: 'overdue', color: C.red, bg: `${C.red}10`, bold: true };
  return { band: 'critical', color: C.red, bg: `${C.red}1f`, bold: true };
}

// ── TYPES ────────────────────────────────────────────────────
export interface AmostraLab {
  id: number;
  fad01_id: number;
  numero_amostra: number;
  lacre_etiqueta: string | null;
  quarteirao: string | null;
  especie_encontrada: string | null;
  larvas: number | null;
  pupas: number | null;
  exuvias: number | null;
  adultos: number | null;
  data_resultado: string | null;
  pendente: boolean;
  observacoes: string | null;
  data_coleta: string | null;
  agente_nome: string | null;
  area_nome: string | null;
  ciclo_nome: string | null;
  se: number | null;
  quarteiroes_trabalhados: string[];
  dias_aguardando: number | null;
  congelada: boolean;
  congelada_em: string | null;
  status: 'pendente' | 'congelada' | 'analisada';
}

export interface Estatisticas {
  total_coletadas: number;
  total_analisadas: number;
  total_pendentes: number;
  total_congeladas?: number;
  total_ativas?: number;
  positivas_aegypti: number;
  positivas_albopictus: number;
  pct_aegypti: number;
  pct_albopictus: number;
  tubitos_hoje?: number;
  tubitos_se_atual?: number;
  se_atual?: number | null;
}

export interface QuarteiraoPositivo {
  id: number;
  quarteirao: string;
  data_coleta: string | null;
  especie: string;
  agente_nome: string | null;
  area_nome: string | null;
  status: string;
  data_resultado: string | null;
}

interface Ciclo { id: number; nome: string; ativo: boolean; }
interface Area { id: number; nome: string; }

interface Props {
  onVoltar: () => void;
  onAbrirFAD01?: (fad01_id: number) => void;
}

// ── ICONS ────────────────────────────────────────────────────
const I = {
  flask: (s = 16) => <svg width={s} height={s} viewBox="0 0 20 20" fill="none"><path d="M8 2v5L4 14a2 2 0 001.7 3h8.6A2 2 0 0016 14l-4-7V2M7 2h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /><path d="M5.4 12h9.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.5" /></svg>,
  back: (s = 14) => <svg width={s} height={s} viewBox="0 0 14 14" fill="none"><path d="M9 11L5 7l4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  calendar: (s = 14) => <svg width={s} height={s} viewBox="0 0 16 16" fill="none"><rect x="2" y="3.5" width="12" height="10.5" rx="1.5" stroke="currentColor" strokeWidth="1.3" /><path d="M2 7h12M5 2v3M11 2v3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" /></svg>,
  alert: (s = 13) => <svg width={s} height={s} viewBox="0 0 14 14" fill="none"><path d="M7 1.5L13 12H1L7 1.5z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" /><path d="M7 6v2.5M7 10v.3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /></svg>,
  shield: (s = 18) => <svg width={s} height={s} viewBox="0 0 20 20" fill="none"><path d="M10 2l7 3v6c0 4-3 6-7 7-4-1-7-3-7-7V5l7-3z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" /><path d="M7 10l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  pin: (s = 13) => <svg width={s} height={s} viewBox="0 0 14 14" fill="none"><path d="M7 1.5c2.5 0 4.5 2 4.5 4.5 0 3-4.5 6.5-4.5 6.5S2.5 9 2.5 6c0-2.5 2-4.5 4.5-4.5z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" /><circle cx="7" cy="6" r="1.4" stroke="currentColor" strokeWidth="1.3" /></svg>,
  download: (s = 14) => <svg width={s} height={s} viewBox="0 0 14 14" fill="none"><path d="M7 2v8M4 7l3 3 3-3M2 12h10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  plus: (s = 13) => <svg width={s} height={s} viewBox="0 0 14 14" fill="none"><path d="M7 3v8M3 7h8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>,
  doc: (s = 13) => <svg width={s} height={s} viewBox="0 0 14 14" fill="none"><path d="M3 1h6l3 3v8a1 1 0 01-1 1H3a1 1 0 01-1-1V2a1 1 0 011-1z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" /><path d="M8 1v3h3M4.5 7h5M4.5 9.5h5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" /></svg>,
  close: (s = 14) => <svg width={s} height={s} viewBox="0 0 14 14" fill="none"><path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>,
  microscope: (s = 18) => <svg width={s} height={s} viewBox="0 0 20 20" fill="none"><path d="M9 3h3l1 2-1 2H9L8 5l1-2z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" /><path d="M10.5 7v5M7 12h7M5 16h12M9 14a3 3 0 003 0" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  sparkles: (s = 14) => <svg width={s} height={s} viewBox="0 0 16 16" fill="none"><path d="M8 2l1.3 3.4L13 7l-3.7 1.6L8 12 6.7 8.6 3 7l3.7-1.6L8 2z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" /><path d="M13 12l.6 1.5L15 14l-1.4.5L13 16l-.6-1.5L11 14l1.4-.5L13 12z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" opacity="0.7" /></svg>,
};

// ── BACKGROUND ───────────────────────────────────────────────
const Background: React.FC = () => (
  <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0, overflow: 'hidden' }}>
    <div style={{
      position: 'absolute', inset: 0,
      background: `
        radial-gradient(ellipse 70% 50% at 15% 0%, rgba(0,184,204,0.05) 0%, transparent 60%),
        radial-gradient(ellipse 60% 50% at 85% 0%, rgba(139,92,246,0.05) 0%, transparent 60%),
        radial-gradient(ellipse 40% 60% at 50% 50%, rgba(13,28,53,0.7) 0%, transparent 100%),
        #030b16`
    }} />
    <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.025 }}>
      <defs>
        <pattern id="lb-dotgrid" x="0" y="0" width="28" height="28" patternUnits="userSpaceOnUse">
          <circle cx="1.5" cy="1.5" r="1.2" fill={C.cyan} />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#lb-dotgrid)" />
    </svg>
  </div>
);

// ── CLUSTER LABEL ────────────────────────────────────────────
const ClusterLabel: React.FC<{ text: string; color?: string; icon?: React.ReactNode }> = ({ text, color = C.gray, icon }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 6,
    fontSize: 9.5, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase',
    color, fontFamily: "'JetBrains Mono', monospace",
    marginBottom: 10,
  }}>
    {icon}
    {text}
  </div>
);

// ── KPI CELL ─────────────────────────────────────────────────
const KpiCell: React.FC<{ label: string; value: string | number; sub: string; color?: string; big?: boolean; urgent?: boolean }> = ({ label, value, sub, color = C.white, big = false, urgent = false }) => (
  <div style={{ flex: 1, minWidth: 0, padding: '2px 0' }}>
    <div style={{
      fontSize: 9, fontWeight: 700, letterSpacing: '0.1em',
      color: C.gray, fontFamily: "'JetBrains Mono', monospace",
      textTransform: 'uppercase', marginBottom: 4,
    }}>{label}</div>
    <div style={{
      fontSize: big ? 32 : 26, fontWeight: 800,
      color, fontFamily: "'JetBrains Mono', monospace",
      lineHeight: 1, letterSpacing: '-0.02em',
      textShadow: urgent ? `0 0 18px ${color}33` : 'none',
    }}>{value}</div>
    <div style={{
      fontSize: 10, color: C.gray, marginTop: 5,
      fontFamily: "'JetBrains Mono', monospace",
      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
    }}>{sub}</div>
  </div>
);

// ── PAGE HEADER ──────────────────────────────────────────────
const PageHeader: React.FC<{ onVoltar: () => void; estatisticas: Estatisticas | null; cicloNome: string; amostras: AmostraLab[] }> = ({ onVoltar, estatisticas, cicloNome, amostras }) => {
  const stats = estatisticas;
  const total = stats?.total_coletadas ?? 0;
  const pendentes = stats?.total_pendentes ?? 0;
  const analisadas = stats?.total_analisadas ?? 0;
  const congeladas = stats?.total_congeladas ?? 0;
  const overdue = useMemo(() => amostras.filter(a => a.status === 'pendente' && (a.dias_aguardando ?? 0) > 7).length, [amostras]);

  return (
    <div style={{
      position: 'relative', overflow: 'hidden',
      padding: '28px 32px 24px',
      background: `linear-gradient(120deg, #060f1e 0%, #0a1828 55%, #06101e 100%)`,
      borderBottom: `1px solid ${C.border}`,
    }}>
      <svg style={{ position: 'absolute', right: 0, top: 0, opacity: 0.04, pointerEvents: 'none' }}
        width="600" height="180" viewBox="0 0 600 180">
        {Array.from({ length: 11 }).map((_, i) => <line key={`v${i}`} x1={i * 60} y1="0" x2={i * 60} y2="180" stroke={C.cyan} strokeWidth="1" />)}
        {Array.from({ length: 5 }).map((_, i) => <line key={`h${i}`} x1="0" y1={i * 45} x2="600" y2={i * 45} stroke={C.cyan} strokeWidth="1" />)}
      </svg>

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 32, flexWrap: 'wrap' }}>
        {/* TÍTULO */}
        <div style={{ minWidth: 280 }}>
          <button onClick={onVoltar} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '6px 12px', marginBottom: 14, borderRadius: 6,
            border: `1px solid ${C.border}`,
            background: 'rgba(255,255,255,0.03)',
            color: C.grayLt, fontSize: 12, fontWeight: 500, cursor: 'pointer',
            fontFamily: "'Inter', sans-serif",
          }}>
            {I.back()}
            Voltar
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{
              width: 46, height: 46, borderRadius: 11,
              background: `linear-gradient(135deg, ${C.purple}30, ${C.purple}08)`,
              border: `1px solid ${C.purpleBorder}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: C.purple,
              boxShadow: `0 0 24px ${C.purple}22, inset 0 1px 0 rgba(255,255,255,0.04)`,
            }}>
              {I.flask(24)}
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <h1 style={{ fontSize: 26, fontWeight: 800, color: C.white, letterSpacing: '-0.02em', lineHeight: 1, fontFamily: "'Inter', sans-serif", margin: 0 }}>Laboratório</h1>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.purple, animation: 'lb-pulse 2.5s infinite', boxShadow: `0 0 8px ${C.purple}` }} />
              </div>
              <div style={{ fontSize: 12, color: C.gray, fontFamily: "'JetBrains Mono', monospace", marginTop: 6 }}>
                Amostras e resultados · {cicloNome}
              </div>
            </div>
          </div>
        </div>

        {/* CLUSTERS */}
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          {/* CLUSTER 1 · OPERACIONAL */}
          <div style={{
            padding: '14px 18px', borderRadius: 12,
            background: `linear-gradient(180deg, rgba(0,184,204,0.06), rgba(0,184,204,0.015))`,
            border: `1px solid ${C.cyanBorder}`,
            minWidth: 340,
          }}>
            <ClusterLabel text="Operacional" color={C.cyan}
              icon={<span style={{ width: 5, height: 5, borderRadius: '50%', background: C.cyan, boxShadow: `0 0 6px ${C.cyan}`, animation: 'lb-pulse 2.5s infinite' }} />} />
            <div style={{ display: 'flex', gap: 24 }}>
              <KpiCell label="Hoje" value={stats?.tubitos_hoje ?? 0} sub="tubitos lançados" color={C.cyan} big />
              <KpiCell label="SE Atual" value={stats?.tubitos_se_atual ?? 0} sub={stats?.se_atual ? `na SE ${stats.se_atual}` : 'na semana'} color={C.white} />
              <KpiCell label="Pendentes" value={pendentes}
                sub={overdue > 0 ? `${overdue} atrasadas (>7d)` : 'aguardando lab'}
                color={overdue > 0 ? C.red : C.amber} urgent={overdue > 0} big />
              <KpiCell label="Congeladas" value={congeladas} sub="fora da fila" color="#7dd3fc" />
            </div>
          </div>

          {/* CLUSTER 2 · PIPELINE */}
          <div style={{
            padding: '14px 18px', borderRadius: 12,
            background: `linear-gradient(180deg, rgba(255,255,255,0.02), transparent)`,
            border: `1px solid ${C.border}`,
            minWidth: 240,
          }}>
            <ClusterLabel text="Pipeline · Ciclo" icon={I.microscope(11)} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <KpiCell label="Coletadas" value={total} sub="tubitos no ciclo" color={C.cyan} />
              <svg width="46" height="44" viewBox="0 0 46 44" style={{ flexShrink: 0, marginTop: 14 }}>
                <defs>
                  <linearGradient id="lb-flowGrad" x1="0%" x2="100%">
                    <stop offset="0%" stopColor={C.cyan} stopOpacity="0.9" />
                    <stop offset="100%" stopColor={C.gray} stopOpacity="0.3" />
                  </linearGradient>
                </defs>
                <path d="M2 22 H38 M30 14 l8 8 -8 8" stroke="url(#lb-flowGrad)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="none" strokeDasharray="3 3" style={{ animation: 'lb-flow 1.8s linear infinite' }} />
              </svg>
              <KpiCell label="Analisadas" value={analisadas} sub="com resultado" color={analisadas === 0 ? C.gray : C.green} />
            </div>
          </div>

          {/* CLUSTER 3 · POSITIVIDADE */}
          <div style={{
            padding: '14px 18px', borderRadius: 12,
            background: `linear-gradient(180deg, rgba(34,197,94,0.05), transparent)`,
            border: `1px solid rgba(34,197,94,0.25)`,
            minWidth: 230,
          }}>
            <ClusterLabel text="Positividade · Espécie" color={C.green} icon={I.shield(11)} />
            <div style={{ display: 'flex', gap: 18 }}>
              <KpiCell label="% Aegypti" value={`${(stats?.pct_aegypti ?? 0).toFixed(1)}%`} sub={`${stats?.positivas_aegypti ?? 0} positivos`} color={(stats?.positivas_aegypti ?? 0) > 0 ? C.red : C.green} />
              <KpiCell label="% Albopictus" value={`${(stats?.pct_albopictus ?? 0).toFixed(1)}%`} sub={`${stats?.positivas_albopictus ?? 0} positivos`} color={(stats?.positivas_albopictus ?? 0) > 0 ? C.orange : C.green} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── FLOAT FIELD ──────────────────────────────────────────────
const FloatField: React.FC<{ label: string; w?: number; children: React.ReactNode }> = ({ label, w = 160, children }) => (
  <div style={{ position: 'relative', minWidth: w }}>
    <label style={{
      position: 'absolute', top: -7, left: 10,
      padding: '0 6px', background: C.bg,
      fontSize: 9.5, fontWeight: 700, letterSpacing: '0.08em',
      color: C.gray, fontFamily: "'JetBrains Mono', monospace",
      textTransform: 'uppercase', zIndex: 1,
    }}>{label}</label>
    {children}
  </div>
);

// ── FILTER BAR ───────────────────────────────────────────────
const FilterBar: React.FC<{
  ciclos: Ciclo[]; areas: Area[];
  cicloId: string; setCicloId: (v: string) => void;
  areaId: string; setAreaId: (v: string) => void;
  status: string; setStatus: (v: string) => void;
  positivas: boolean; setPositivas: (v: boolean) => void;
  dataInicio: string; setDataInicio: (v: string) => void;
  dataFim: string; setDataFim: (v: string) => void;
  onExportar: () => void;
}> = ({ ciclos, areas, cicloId, setCicloId, areaId, setAreaId, status, setStatus, positivas, setPositivas, dataInicio, setDataInicio, dataFim, setDataFim, onExportar }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
    padding: '18px 18px',
    background: C.bgPanel,
    border: `1px solid ${C.border}`,
    borderRadius: 12,
    marginBottom: 18,
  }}>
    <FloatField label="Ciclo" w={160}>
      <select className="lab-input" style={{ width: 160 }} value={cicloId} onChange={e => setCicloId(e.target.value)}>
        {ciclos.map(c => <option key={c.id} value={c.id}>{c.nome}{c.ativo ? ' (ativo)' : ''}</option>)}
      </select>
    </FloatField>

    <FloatField label="Área" w={180}>
      <select className="lab-input" style={{ width: 180 }} value={areaId} onChange={e => setAreaId(e.target.value)}>
        <option value="">Todas</option>
        {areas.map(a => <option key={a.id} value={a.id}>{a.nome}</option>)}
      </select>
    </FloatField>

    <FloatField label="Status" w={160}>
      <select className="lab-input" style={{ width: 160 }} value={status} onChange={e => setStatus(e.target.value)}>
        <option value="">Todas</option>
        <option value="pendente">Pendentes</option>
        <option value="analisada">Analisadas</option>
        <option value="congelada">Congeladas</option>
      </select>
    </FloatField>

    <FloatField label="De" w={140}>
      <input type="date" className="lab-input" style={{ width: 140 }} value={dataInicio} onChange={e => setDataInicio(e.target.value)} />
    </FloatField>

    <FloatField label="Até" w={140}>
      <input type="date" className="lab-input" style={{ width: 140 }} value={dataFim} onChange={e => setDataFim(e.target.value)} />
    </FloatField>

    <label style={{
      display: 'inline-flex', alignItems: 'center', gap: 8,
      padding: '8px 12px', borderRadius: 7,
      border: `1px solid ${positivas ? C.red : C.border}`,
      background: positivas ? `${C.red}1a` : 'rgba(255,255,255,0.025)',
      color: positivas ? C.red : C.grayLt,
      fontSize: 12, fontWeight: 600, cursor: 'pointer',
      transition: 'all 0.15s',
    }}>
      <span style={{
        width: 14, height: 14, borderRadius: 3,
        border: `1.5px solid ${positivas ? C.red : C.gray}`,
        background: positivas ? C.red : 'transparent',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {positivas && <svg width="10" height="10" viewBox="0 0 10 10"><path d="M1.5 5l2.5 2.5L8.5 2.5" stroke="#021018" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="none" /></svg>}
      </span>
      <input type="checkbox" checked={positivas} onChange={(e) => setPositivas(e.target.checked)} style={{ display: 'none' }} />
      Só positivas
    </label>

    <div style={{ flex: 1, minWidth: 8 }} />

    <button onClick={onExportar} style={{
      display: 'inline-flex', alignItems: 'center', gap: 8,
      height: 36, padding: '0 16px', borderRadius: 8,
      border: `1px solid ${C.green}66`,
      background: `${C.green}14`,
      color: C.green, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
      fontFamily: "'Inter', sans-serif",
    }}>
      {I.download(13)}
      Exportar CSV
    </button>
  </div>
);

// ── SPECIES BADGE ────────────────────────────────────────────
const SpeciesBadge: React.FC<{ especie: string | null }> = ({ especie }) => {
  if (!especie) return <span style={{ color: 'rgba(255,255,255,0.16)', fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>—</span>;
  const map: Record<string, { label: string; color: string }> = {
    aegypti: { label: 'Aegypti', color: C.red },
    albopictus: { label: 'Albopictus', color: C.orange },
    outros: { label: 'Outros', color: C.lavender },
    negativo: { label: 'Negativo', color: C.green },
    nao_identificado: { label: 'N/I', color: C.gray },
  };
  const m = map[especie] || { label: especie, color: C.gray };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 9px', borderRadius: 5,
      background: `${m.color}1a`,
      border: `1px solid ${m.color}55`,
      color: m.color, fontSize: 10.5, fontWeight: 700,
      letterSpacing: '0.04em',
      fontFamily: "'JetBrains Mono', monospace",
    }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: m.color, boxShadow: `0 0 5px ${m.color}` }} />
      {m.label}
    </span>
  );
};

// ── STATUS BADGE ─────────────────────────────────────────────
const StatusBadge: React.FC<{ status: 'pendente' | 'congelada' | 'analisada'; dias: number | null; dataResultado?: string | null; congeladaEm?: string | null }> = ({ status, dias, dataResultado, congeladaEm }) => {
  const pendente = status === 'pendente';
  const congelada = status === 'congelada';
  const u = pendente ? urgency(dias) : null;
  const color = congelada ? '#7dd3fc' : pendente ? C.amber : C.green;
  const congFmt = congelada && congeladaEm
    ? new Date(congeladaEm).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' })
    : null;
  const dataFmt = !pendente && !congelada && dataResultado
    ? new Date(dataResultado + 'T00:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' })
    : null;
  return (
    <div>
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '3px 9px', borderRadius: 5,
        background: `${color}1a`,
        border: `1px solid ${color}55`,
        color, fontSize: 10, fontWeight: 800,
        letterSpacing: '0.08em',
        fontFamily: "'JetBrains Mono', monospace",
        textTransform: 'uppercase',
      }}>
        <span style={{
          width: 5, height: 5, borderRadius: '50%', background: color,
          animation: pendente ? 'lb-pulse 2s infinite' : 'none',
          boxShadow: `0 0 5px ${color}`,
        }} />
        {congelada ? 'Congelada' : pendente ? 'Pendente' : 'Analisada'}
      </span>
      {dataFmt && (
        <div style={{
          marginTop: 4,
          fontSize: 10.5, color: '#6b7a8d',
          fontFamily: "'JetBrains Mono', monospace",
          letterSpacing: '0.03em',
        }}>
          {dataFmt}
        </div>
      )}
      {congFmt && (
        <div style={{ marginTop: 4, fontSize: 10.5, color: '#6b7a8d', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.03em' }}>
          congelada em {congFmt}
        </div>
      )}
      {pendente && u && dias !== null && dias !== undefined && (
        <div style={{
          marginTop: 5,
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: u.band === 'overdue' || u.band === 'critical' ? '2px 7px' : '0',
          borderRadius: 4,
          background: u.bg,
          border: (u.band === 'overdue' || u.band === 'critical') ? `1px solid ${u.color}55` : 'none',
          fontSize: 10.5, fontWeight: u.bold ? 800 : 600,
          color: u.color,
          fontFamily: "'JetBrains Mono', monospace",
          letterSpacing: u.bold ? '0.04em' : '0',
          animation: u.band === 'critical' ? 'lb-overdueRing 1.6s ease-out infinite' : 'none',
        }}>
          {(u.band === 'overdue' || u.band === 'critical') && I.alert(10)}
          há {dias} {dias === 1 ? 'dia' : 'dias'}
        </div>
      )}
    </div>
  );
};

// ── SAMPLE ROW ───────────────────────────────────────────────
const SampleRow: React.FC<{ s: AmostraLab; onEditar: (s: AmostraLab) => void; onAbrirFAD01?: (id: number) => void; onCongelar?: (s: AmostraLab) => void; alterandoId?: number | null }> = ({ s, onEditar, onAbrirFAD01, onCongelar, alterandoId }) => {
  const overdue = s.status === 'pendente' && (s.dias_aguardando ?? 0) > 7;
  const ocupado = alterandoId === s.id;
  const trabSlice = (s.quarteiroes_trabalhados || []).slice(0, 3);
  const trabExtras = (s.quarteiroes_trabalhados || []).length - trabSlice.length;

  return (
    <tr style={{
      background: overdue ? 'rgba(239,68,68,0.025)' : undefined,
      borderLeft: overdue ? `2px solid ${C.red}88` : '2px solid transparent',
    }}>
      {/* # / FAD */}
      <td style={{ minWidth: 78, paddingLeft: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.white, fontFamily: "'JetBrains Mono', monospace" }}>#{s.numero_amostra}</div>
        <div style={{ fontSize: 10, color: C.gray, fontFamily: "'JetBrains Mono', monospace", marginTop: 2 }}>FAD {s.fad01_id}</div>
      </td>

      {/* DATA COLETA */}
      <td style={{ fontFamily: "'JetBrains Mono', monospace", color: C.white, fontWeight: 500 }}>
        {fmtDataBR(s.data_coleta)}
      </td>

      {/* SE */}
      <td>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 3,
          fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700,
          color: C.cyan, letterSpacing: '0.04em',
        }}>
          <span style={{ color: C.gray, fontWeight: 500 }}>SE</span>
          {s.se ?? '—'}
        </span>
      </td>

      {/* AGENTE */}
      <td style={{ color: C.white, fontWeight: 500 }}>{s.agente_nome || '—'}</td>

      {/* ÁREA */}
      <td style={{ color: C.grayLt }}>{s.area_nome || '—'}</td>

      {/* QUART. TRABALH. */}
      <td>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {trabSlice.map((q, i) => (
            <span key={`${q}-${i}`} style={{
              padding: '2px 7px', borderRadius: 4,
              background: 'rgba(255,255,255,0.04)',
              border: `1px solid ${C.borderMid}`,
              color: C.grayLt, fontSize: 10.5, fontWeight: 600,
              fontFamily: "'JetBrains Mono', monospace",
            }}>Q-{q}</span>
          ))}
          {trabExtras > 0 && (
            <span style={{
              padding: '2px 7px', borderRadius: 4,
              background: 'rgba(0,184,204,0.10)',
              border: `1px solid ${C.cyanBorder}`,
              color: C.cyan, fontSize: 10.5, fontWeight: 700,
              fontFamily: "'JetBrains Mono', monospace",
            }}>+{trabExtras}</span>
          )}
          {trabSlice.length === 0 && (
            <span style={{ color: 'rgba(255,255,255,0.16)', fontSize: 12, fontFamily: "'JetBrains Mono', monospace" }}>—</span>
          )}
        </div>
      </td>

      {/* LACRE */}
      <td>
        {s.lacre_etiqueta ? (
          <span style={{ fontFamily: "'JetBrains Mono', monospace", color: C.white, fontSize: 11.5, fontWeight: 600 }}>{s.lacre_etiqueta}</span>
        ) : (
          <span style={{ color: 'rgba(255,255,255,0.16)', fontSize: 14, fontFamily: "'JetBrains Mono', monospace" }}>—</span>
        )}
      </td>

      {/* QUART. RESULT. */}
      <td>
        {s.quarteirao ? (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '2px 7px', borderRadius: 4,
            background: `${C.red}1a`, border: `1px solid ${C.red}66`,
            color: C.red, fontSize: 11, fontWeight: 800,
            fontFamily: "'JetBrains Mono', monospace",
          }}>{I.pin(10)}Q-{s.quarteirao}</span>
        ) : (
          <span style={{ color: 'rgba(255,255,255,0.16)', fontSize: 14, fontFamily: "'JetBrains Mono', monospace" }}>—</span>
        )}
      </td>

      {/* ESPÉCIE */}
      <td><SpeciesBadge especie={s.especie_encontrada} /></td>

      {/* L · P · E · A */}
      <td>
        {s.larvas !== null || s.pupas !== null || s.exuvias !== null || s.adultos !== null ? (
          <span style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11.5, fontWeight: 600,
            color: C.white,
            letterSpacing: '0.05em',
          }}>
            {s.larvas ?? '—'}<span style={{ color: C.gray, margin: '0 4px' }}>·</span>
            {s.pupas ?? '—'}<span style={{ color: C.gray, margin: '0 4px' }}>·</span>
            {s.exuvias ?? '—'}<span style={{ color: C.gray, margin: '0 4px' }}>·</span>
            {s.adultos ?? '—'}
          </span>
        ) : (
          <span style={{ color: 'rgba(255,255,255,0.16)', fontSize: 11, fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.18em' }}>
            — · — · — · —
          </span>
        )}
      </td>

      {/* STATUS */}
      <td style={{ minWidth: 140 }}><StatusBadge status={s.status} dias={s.dias_aguardando} dataResultado={s.data_resultado} congeladaEm={s.congelada_em} /></td>

      {/* AÇÕES */}
      <td style={{ paddingRight: 14 }}>
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
          {onCongelar && s.status !== 'analisada' && (
            <button
              onClick={() => onCongelar(s)}
              disabled={ocupado}
              title={s.status === 'congelada' ? 'Voltar a contar como pendente' : 'Tirar da fila operacional (não conta como pendente)'}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '6px 12px', borderRadius: 6,
                border: `1px solid ${s.status === 'congelada' ? '#7dd3fc' : C.border}`,
                background: s.status === 'congelada' ? 'rgba(125,211,252,0.12)' : 'rgba(255,255,255,0.03)',
                color: s.status === 'congelada' ? '#7dd3fc' : C.grayLt,
                fontSize: 11, fontWeight: 700, cursor: ocupado ? 'wait' : 'pointer',
                fontFamily: 'Inter,sans-serif', opacity: ocupado ? 0.6 : 1,
              }}>
              {ocupado ? '…' : (s.status === 'congelada' ? '🔓 Descongelar' : '❄️ Congelar')}
            </button>
          )}
          <button
            onClick={() => onAbrirFAD01 && onAbrirFAD01(s.fad01_id)}
            disabled={!onAbrirFAD01}
            title={s.status === 'pendente' ? 'Lançar resultado no FAD-01' : 'Editar resultado no FAD-01'}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '6px 12px', borderRadius: 6,
              border: `1px solid ${s.status === 'pendente' ? C.cyan : C.border}`,
              background: s.status === 'pendente' ? C.cyan : 'rgba(255,255,255,0.03)',
              color: s.status === 'pendente' ? '#021018' : C.grayLt,
              fontSize: 11, fontWeight: 700, cursor: onAbrirFAD01 ? 'pointer' : 'not-allowed',
              fontFamily: 'Inter,sans-serif',
              boxShadow: s.status === 'pendente' ? `0 2px 6px ${C.cyan}44` : 'none',
              opacity: onAbrirFAD01 ? 1 : 0.5,
            }}>
            {I.doc(11)}{s.status === 'pendente' ? 'Editar no FAD-01' : 'Ver no FAD-01'}
          </button>
        </div>
      </td>
    </tr>
  );
};

// ── SAMPLES TABLE ────────────────────────────────────────────
const SamplesTable: React.FC<{ amostras: AmostraLab[]; loading: boolean; onEditar: (s: AmostraLab) => void; onAbrirFAD01?: (id: number) => void; onCongelar?: (s: AmostraLab) => void; alterandoId?: number | null }> = ({ amostras, loading, onEditar, onAbrirFAD01, onCongelar, alterandoId }) => {
  if (loading) {
    return (
      <div style={{
        padding: 60, textAlign: 'center',
        background: C.bgPanel, border: `1px solid ${C.border}`, borderRadius: 12,
        color: C.gray, fontSize: 12, fontFamily: "'JetBrains Mono', monospace",
      }}>Carregando amostras...</div>
    );
  }
  if (amostras.length === 0) {
    return (
      <div style={{
        padding: '48px 24px', textAlign: 'center',
        background: C.bgPanel, border: `1px solid ${C.border}`, borderRadius: 12,
      }}>
        <div style={{ fontSize: 14, color: C.grayLt, fontFamily: "'Inter', sans-serif", marginBottom: 6 }}>
          Nenhuma amostra encontrada
        </div>
        <div style={{ fontSize: 11.5, color: C.gray, fontFamily: "'JetBrains Mono', monospace" }}>
          Ajuste os filtros ou cadastre amostras no FAD-01.
        </div>
      </div>
    );
  }

  return (
    <div style={{
      background: C.bgPanel,
      border: `1px solid ${C.border}`,
      borderRadius: 12,
      overflow: 'hidden',
      boxShadow: '0 4px 18px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.03)',
      animation: 'lb-fadeIn 0.35s ease',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 18px',
        borderBottom: `1px solid ${C.border}`,
        background: 'rgba(0,184,204,0.025)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: C.cyan, animation: 'lb-pulse 2.5s infinite' }} />
          <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.1em', color: C.cyan, textTransform: 'uppercase', fontFamily: "'JetBrains Mono', monospace" }}>
            Tubitos · {amostras.length} registros
          </span>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', fontSize: 10.5, color: C.gray, fontFamily: "'JetBrains Mono', monospace" }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: C.amber, opacity: 0.85 }} /> Pendente
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: C.red, opacity: 0.85 }} /> &gt; 7 dias = atraso
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 8, height: 8, borderRadius: 2, background: C.green, opacity: 0.85 }} /> Analisada
          </span>
        </div>
      </div>

      <div className="lab-scroll" style={{ maxHeight: 'calc((100vh - 360px) * 3)', overflowY: 'auto', overflowX: 'hidden' }}>
        <table className="lab-table" style={{ tableLayout: 'auto', width: '100%' }}>
          <thead>
            <tr>
              <th style={{ paddingLeft: 14 }}>#</th>
              <th>Data Coleta</th>
              <th>SE</th>
              <th>Agente</th>
              <th>Área</th>
              <th>Quart. Trabalh.</th>
              <th>Lacre</th>
              <th>Quart. Result.</th>
              <th>Espécie</th>
              <th>L · P · E · A</th>
              <th>Status</th>
              <th style={{ paddingRight: 14, textAlign: 'right' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {amostras.map((s, i) => <SampleRow key={`${s.id}-${i}`} s={s} onEditar={onEditar} onAbrirFAD01={onAbrirFAD01} onCongelar={onCongelar} alterandoId={alterandoId} />)}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── POSITIVES PANEL ──────────────────────────────────────────
const PositivesPanel: React.FC<{ positivos: QuarteiraoPositivo[]; cicloNome: string }> = ({ positivos, cicloNome }) => {
  if (positivos.length === 0) {
    return null;
  }

  return (
    <div style={{
      background: `linear-gradient(180deg, rgba(239,68,68,0.08) 0%, rgba(239,68,68,0.015) 100%)`,
      border: `1px solid rgba(239,68,68,0.35)`,
      borderRadius: 12,
      overflow: 'hidden',
      animation: 'lb-fadeIn 0.4s ease',
    }}>
      <div style={{
        padding: '14px 18px',
        borderBottom: `1px solid rgba(239,68,68,0.20)`,
        background: 'rgba(239,68,68,0.08)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            width: 8, height: 8, borderRadius: '50%', background: C.red,
            boxShadow: `0 0 8px ${C.red}`, animation: 'lb-pulseStrong 1.4s infinite',
          }} />
          <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.1em', color: C.red, textTransform: 'uppercase', fontFamily: "'JetBrains Mono', monospace" }}>
            Quarteirões Positivos
          </span>
        </div>
        <span style={{
          padding: '2px 8px', borderRadius: 4,
          background: C.red, color: '#fff',
          fontSize: 11, fontWeight: 800, fontFamily: "'JetBrains Mono', monospace",
        }}>{positivos.length}</span>
      </div>
      <div style={{ padding: '14px 14px 16px', display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 480, overflowY: 'auto' }}>
        {positivos.map((p) => {
          const sp = p.especie === 'aegypti'
            ? { color: C.red, label: 'Aegypti' }
            : p.especie === 'albopictus' ? { color: C.orange, label: 'Albopictus' } : { color: C.lavender, label: p.especie };
          return (
            <div key={p.id} style={{
              padding: '12px 14px',
              borderRadius: 10,
              background: 'rgba(0,0,0,0.25)',
              border: `1px solid rgba(255,255,255,0.06)`,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '4px 10px', borderRadius: 6,
                  background: `${sp.color}1f`,
                  border: `1px solid ${sp.color}66`,
                  color: sp.color, fontSize: 13, fontWeight: 800,
                  fontFamily: "'JetBrains Mono', monospace",
                }}>
                  {I.pin(12)}Q-{p.quarteirao}
                </span>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '3px 8px', borderRadius: 5,
                  background: `${sp.color}1a`,
                  border: `1px solid ${sp.color}44`,
                  color: sp.color, fontSize: 10, fontWeight: 700,
                  letterSpacing: '0.04em',
                  fontFamily: "'JetBrains Mono', monospace",
                }}>{sp.label}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontSize: 11, color: C.grayLt, fontFamily: "'JetBrains Mono', monospace" }}>
                <span><span style={{ color: C.gray }}>Área:</span> {p.area_nome || '—'}</span>
                <span><span style={{ color: C.gray }}>Coleta:</span> {fmtDataBR(p.data_coleta)}</span>
                <span><span style={{ color: C.gray }}>Agente:</span> {p.agente_nome || '—'}</span>
                <span><span style={{ color: C.gray }}>Result.:</span> {fmtDataBR(p.data_resultado)}</span>
              </div>
            </div>
          );
        })}
      </div>
      <div style={{
        padding: '10px 16px',
        borderTop: `1px solid rgba(239,68,68,0.18)`,
        fontSize: 11, color: C.grayLt, lineHeight: 1.5,
        display: 'flex', alignItems: 'center', gap: 6,
        background: 'rgba(239,68,68,0.04)',
      }}>
        {I.alert(11)}
        <strong style={{ color: C.red }}>Atenção:</strong> priorizar visitas de combate nestes quarteirões.
      </div>
    </div>
  );
};

// ── MODAL FIELD ──────────────────────────────────────────────
const Field: React.FC<{ label: string; hint?: string; children: React.ReactNode; full?: boolean }> = ({ label, hint, children, full }) => (
  <div style={{ gridColumn: full ? '1 / -1' : 'auto' }}>
    <div style={{
      fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
      color: C.gray, fontFamily: "'JetBrains Mono', monospace",
      textTransform: 'uppercase', marginBottom: 6,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    }}>
      <span>{label}</span>
      {hint && <span style={{ color: 'rgba(122,135,153,0.7)', fontWeight: 500, letterSpacing: '0.02em', textTransform: 'none' }}>{hint}</span>}
    </div>
    {children}
  </div>
);

// ── LANCAR MODAL ─────────────────────────────────────────────
const LancarModal: React.FC<{ amostra: AmostraLab | null; onFechar: () => void; onSalvar: (id: number, payload: any) => Promise<void> }> = ({ amostra, onFechar, onSalvar }) => {
  const [lacre, setLacre] = useState('');
  const [quarteirao, setQuarteirao] = useState('');
  const [especie, setEspecie] = useState('');
  const [larvas, setLarvas] = useState('');
  const [pupas, setPupas] = useState('');
  const [exuvias, setExuvias] = useState('');
  const [adultos, setAdultos] = useState('');
  const [dataResultado, setDataResultado] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  useEffect(() => {
    if (amostra) {
      setLacre(amostra.lacre_etiqueta || '');
      setQuarteirao(amostra.quarteirao || '');
      setEspecie(amostra.especie_encontrada || '');
      setLarvas(amostra.larvas !== null && amostra.larvas !== undefined ? String(amostra.larvas) : '');
      setPupas(amostra.pupas !== null && amostra.pupas !== undefined ? String(amostra.pupas) : '');
      setExuvias(amostra.exuvias !== null && amostra.exuvias !== undefined ? String(amostra.exuvias) : '');
      setAdultos(amostra.adultos !== null && amostra.adultos !== undefined ? String(amostra.adultos) : '');
      setDataResultado(amostra.data_resultado || '');
      setObservacoes(amostra.observacoes || '');
      setErro('');
    }
  }, [amostra]);

  if (!amostra) return null;

  const handleSalvar = async () => {
    setSalvando(true);
    setErro('');
    try {
      const payload: any = {
        lacre_etiqueta: lacre || null,
        quarteirao: quarteirao || null,
        especie_encontrada: especie || null,
        larvas: larvas === '' ? null : parseInt(larvas, 10),
        pupas: pupas === '' ? null : parseInt(pupas, 10),
        exuvias: exuvias === '' ? null : parseInt(exuvias, 10),
        adultos: adultos === '' ? null : parseInt(adultos, 10),
        data_resultado: dataResultado || null,
        observacoes: observacoes || null,
      };
      await onSalvar(amostra.id, payload);
      onFechar();
    } catch (e: any) {
      setErro(e?.response?.data?.detail || e?.message || 'Erro ao salvar');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 200,
      background: 'rgba(3,11,22,0.78)',
      backdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '32px', animation: 'lb-fadeIn 0.2s ease',
    }} onClick={onFechar}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: 'min(720px, 100%)',
        maxHeight: 'calc(100vh - 64px)',
        overflow: 'auto',
        background: `linear-gradient(180deg, ${C.bgPanel} 0%, #050e1c 100%)`,
        border: `1px solid ${C.purpleBorder}`,
        borderRadius: 14,
        boxShadow: `0 30px 80px rgba(0,0,0,0.6), 0 0 0 1px ${C.purple}22, inset 0 1px 0 rgba(255,255,255,0.04)`,
      }}>
        <div style={{ height: 3, background: `linear-gradient(90deg, ${C.purple}, ${C.cyan})` }} />

        <div style={{
          padding: '20px 24px 18px', borderBottom: `1px solid ${C.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10,
              background: `linear-gradient(135deg, ${C.purple}33, ${C.purple}08)`,
              border: `1px solid ${C.purpleBorder}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: C.purple,
            }}>{I.flask(20)}</div>
            <div>
              <div style={{ fontSize: 18, fontWeight: 800, color: C.white, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
                {amostra.pendente ? 'Lançar resultado' : 'Editar amostra'} <span style={{ color: C.purple, fontFamily: "'JetBrains Mono', monospace" }}>#{amostra.numero_amostra}</span>
              </div>
              <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 12, fontSize: 11.5, color: C.gray, fontFamily: "'JetBrains Mono', monospace", flexWrap: 'wrap' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>{I.doc(11)}FAD {amostra.fad01_id}</span>
                <span style={{ width: 1, height: 11, background: C.border }} />
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>{I.calendar(11)}{fmtDataBR(amostra.data_coleta)}</span>
                <span style={{ width: 1, height: 11, background: C.border }} />
                <span>{amostra.agente_nome || '—'} · {amostra.area_nome || '—'}</span>
              </div>
            </div>
          </div>
          <button onClick={onFechar} style={{
            width: 32, height: 32, borderRadius: 8,
            border: `1px solid ${C.border}`,
            background: 'rgba(255,255,255,0.03)',
            color: C.gray, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>{I.close()}</button>
        </div>

        {amostra.quarteiroes_trabalhados && amostra.quarteiroes_trabalhados.length > 0 && (
          <div style={{
            margin: '18px 24px 0',
            padding: '12px 14px',
            background: `linear-gradient(180deg, rgba(0,184,204,0.10), rgba(0,184,204,0.03))`,
            border: `1px solid ${C.cyanBorder}`,
            borderRadius: 10,
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <div style={{
              width: 30, height: 30, borderRadius: '50%',
              background: `${C.cyan}22`, color: C.cyan,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>{I.pin(14)}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: C.cyan, textTransform: 'uppercase', fontFamily: "'JetBrains Mono', monospace", marginBottom: 4 }}>
                Quarteirões trabalhados na visita
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {amostra.quarteiroes_trabalhados.map((q, i) => (
                  <span key={`${q}-${i}`} style={{
                    padding: '3px 9px', borderRadius: 5,
                    background: 'rgba(0,0,0,0.25)',
                    border: `1px solid ${C.cyanBorder}`,
                    color: C.cyan, fontSize: 11.5, fontWeight: 700,
                    fontFamily: "'JetBrains Mono', monospace",
                  }}>Q-{q}</span>
                ))}
              </div>
            </div>
          </div>
        )}

        <div style={{ padding: '18px 24px 0' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Field label="Lacre / Etiqueta" hint="ex: VL-2026-0123">
              <input className="lab-input" style={{ width: '100%' }} placeholder="VL-2026-…" value={lacre} onChange={e => setLacre(e.target.value)} />
            </Field>
            <Field label="Quarteirão da amostra" hint="positivo confirmado">
              <input className="lab-input" style={{ width: '100%' }} placeholder="Ex: 12" value={quarteirao} onChange={e => setQuarteirao(e.target.value)} />
            </Field>
            <Field label="Espécie encontrada">
              <select className="lab-input" style={{ width: '100%' }} value={especie} onChange={e => setEspecie(e.target.value)}>
                <option value="">— Selecione —</option>
                <option value="aegypti">Aedes aegypti</option>
                <option value="albopictus">Aedes albopictus</option>
                <option value="outros">Outros</option>
                <option value="negativo">Negativo</option>
                <option value="nao_identificado">Não identificado</option>
              </select>
            </Field>
            <Field label="Data do resultado">
              <input type="date" className="lab-input" style={{ width: '100%' }} value={dataResultado} onChange={e => setDataResultado(e.target.value)} />
            </Field>
            <Field label="Larvas">
              <input type="number" className="lab-input" style={{ width: '100%' }} placeholder="0" min={0} value={larvas} onChange={e => setLarvas(e.target.value)} />
            </Field>
            <Field label="Pupas">
              <input type="number" className="lab-input" style={{ width: '100%' }} placeholder="0" min={0} value={pupas} onChange={e => setPupas(e.target.value)} />
            </Field>
            <Field label="Exúvias">
              <input type="number" className="lab-input" style={{ width: '100%' }} placeholder="0" min={0} value={exuvias} onChange={e => setExuvias(e.target.value)} />
            </Field>
            <Field label="Adultos">
              <input type="number" className="lab-input" style={{ width: '100%' }} placeholder="0" min={0} value={adultos} onChange={e => setAdultos(e.target.value)} />
            </Field>
            <Field label="Observações" full>
              <textarea className="lab-input" style={{ width: '100%', height: 74, padding: '10px 12px', resize: 'vertical', fontFamily: 'Inter,sans-serif' }} placeholder="Notas adicionais do laboratório, condições da amostra, etc." value={observacoes} onChange={e => setObservacoes(e.target.value)} />
            </Field>
          </div>

          {erro && (
            <div style={{
              marginTop: 14, padding: '10px 14px',
              background: `${C.red}15`, border: `1px solid ${C.red}55`, borderRadius: 8,
              fontSize: 12, color: C.red, fontFamily: "'Inter', sans-serif",
            }}>{erro}</div>
          )}
        </div>

        <div style={{
          padding: '16px 24px 20px', marginTop: 14,
          borderTop: `1px solid ${C.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <div style={{ fontSize: 11, color: C.gray, fontFamily: "'JetBrains Mono', monospace" }}>
            Após salvar, este registro entra em <strong style={{ color: C.green }}>ANALISADA</strong>.
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={onFechar} disabled={salvando} style={{
              padding: '9px 16px', borderRadius: 8,
              border: `1px solid ${C.border}`,
              background: 'rgba(255,255,255,0.03)',
              color: C.grayLt, fontSize: 12.5, fontWeight: 600,
              cursor: salvando ? 'not-allowed' : 'pointer',
            }}>Cancelar</button>
            <button onClick={handleSalvar} disabled={salvando} style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              padding: '9px 18px', borderRadius: 8,
              border: `1px solid ${C.purple}`,
              background: `linear-gradient(135deg, ${C.purple}, #7c4ce0)`,
              color: '#fff', fontSize: 12.5, fontWeight: 800,
              cursor: salvando ? 'not-allowed' : 'pointer',
              boxShadow: `0 4px 14px ${C.purple}55`,
            }}>
              {I.flask(13)}{salvando ? 'Salvando...' : 'Salvar resultado'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── ROOT ─────────────────────────────────────────────────────
const Laboratorio: React.FC<Props> = ({ onVoltar, onAbrirFAD01 }) => {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: 'Bearer ' + token }), [token]);

  const [ciclos, setCiclos] = useState<Ciclo[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [cicloId, setCicloId] = useState<string>('');
  const [areaId, setAreaId] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [positivas, setPositivas] = useState(false);
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');

  const [amostras, setAmostras] = useState<AmostraLab[]>([]);
  const [estatisticas, setEstatisticas] = useState<Estatisticas | null>(null);
  const [positivos, setPositivos] = useState<QuarteiraoPositivo[]>([]);

  const [loading, setLoading] = useState(false);
  const [editando, setEditando] = useState<AmostraLab | null>(null);
  const [alterandoId, setAlterandoId] = useState<number | null>(null);
  const [pendenciasResumo, setPendenciasResumo] = useState<{ total_orfaos: number; orfaos: any[] } | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [ciclosR, areasR] = await Promise.all([
          axios.get(`${API_URL}/api/ciclos/`, { headers }),
          axios.get(`${API_URL}/api/areas/`, { headers }),
        ]);
        const cs: Ciclo[] = ciclosR.data || [];
        setCiclos(cs);
        setAreas(areasR.data || []);
        const ativo = cs.find(c => c.ativo);
        if (ativo) setCicloId(String(ativo.id));
        else if (cs.length > 0) setCicloId(String(cs[0].id));
      } catch (e) {
        console.error('Erro ao carregar ciclos/áreas', e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buscar = useCallback(async () => {
    if (!cicloId) return;
    setLoading(true);
    try {
      const params: any = { ciclo_id: cicloId };
      if (areaId) params.area_id = areaId;
      if (status) params.status = status;
      if (positivas) params.positivas = true;
      if (dataInicio) params.data_inicio = dataInicio;
      if (dataFim) params.data_fim = dataFim;
      const [aR, eR, pR] = await Promise.all([
        axios.get(`${API_URL}/api/laboratorio/amostras`, { params, headers }),
        axios.get(`${API_URL}/api/laboratorio/estatisticas`, { params: { ciclo_id: cicloId }, headers }),
        axios.get(`${API_URL}/api/laboratorio/quarteiroes-positivos`, { params: { ciclo_id: cicloId }, headers }),
      ]);
      setAmostras(aR.data || []);
      setEstatisticas(eR.data || null);
      setPositivos(pR.data || []);
    } catch (e) {
      console.error('Erro ao carregar amostras', e);
    } finally {
      setLoading(false);
    }
  }, [cicloId, areaId, status, positivas, dataInicio, dataFim, headers]);

  useEffect(() => { buscar(); }, [buscar]);

  useEffect(() => {
    if (!token) return;
    const fetchOrfaos = () => {
      axios.get(`${API_URL}/api/laboratorio/pendencias-resumo`, { headers })
        .then(r => setPendenciasResumo(r.data))
        .catch(() => setPendenciasResumo(null));
    };
    fetchOrfaos();
    const iv = setInterval(fetchOrfaos, 60000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const sincronizarOrfaos = async () => {
    if (!token) return;
    if (!window.confirm('Sincronizar amostras órfãs com o laboratório? Isso cria as linhas faltantes.')) return;
    try {
      const r = await axios.post(`${API_URL}/api/laboratorio/sincronizar-orfaos`, {}, { headers });
      alert(`✅ Sincronizados ${r.data.corrigidos} FAD-01.\nErros: ${r.data.erros?.length || 0}`);
      window.location.reload();
    } catch (e: any) {
      alert('❌ Erro ao sincronizar: ' + (e?.message || 'desconhecido'));
    }
  };

  const handleSalvar = async (id: number, payload: any) => {
    await axios.put(`${API_URL}/api/laboratorio/amostras/${id}`, payload, { headers });
    await buscar();
  };

  // Congelar / descongelar (Sessao 23) - acao individual, confirmada, sem atualizacao otimista
  const handleCongelar = async (s: AmostraLab) => {
    if (alterandoId !== null) return;
    const congelar = s.status !== 'congelada';
    const msg = congelar
      ? `Congelar a amostra #${s.numero_amostra} (FAD-01 ${s.fad01_id})?\n\nEla deixará de contar como pendente e não gerará alerta de atraso, mas continuará registrada no histórico.`
      : `Descongelar a amostra #${s.numero_amostra} (FAD-01 ${s.fad01_id})?\n\nEla voltará a contar como pendente, e o tempo de espera será calculado desde a data original da coleta.`;
    if (!window.confirm(msg)) return;
    setAlterandoId(s.id);
    try {
      await axios.patch(`${API_URL}/api/laboratorio/amostras/${s.id}/congelamento`, { congelada: congelar }, { headers });
      await buscar();
    } catch (e: any) {
      const st = e?.response?.status;
      const det = e?.response?.data?.detail;
      const texto = st === 401 ? 'Sua sessão expirou. Entre novamente.'
        : st === 403 ? 'Você não tem permissão para isso (lab.editar).'
        : st === 404 ? 'Amostra não encontrada. Atualize a tela.'
        : st === 409 ? ('Não é possível: ' + (det || 'a amostra já foi analisada.'))
        : ('Erro: ' + (det || e?.message || 'falha de rede.'));
      alert('❌ ' + texto);
    } finally {
      setAlterandoId(null);
    }
  };

  const exportarCSV = () => {
    if (amostras.length === 0) return;
    const headersCsv = ['#', 'FAD-01', 'Data Coleta', 'SE', 'Agente', 'Área', 'Quart. Trabalhados', 'Lacre', 'Quart. Resultado', 'Espécie', 'Larvas', 'Pupas', 'Exúvias', 'Adultos', 'Data Resultado', 'Status', 'Dias Aguardando', 'Congelada em'];
    const rows = amostras.map(a => [
      a.numero_amostra, a.fad01_id, a.data_coleta || '', a.se ?? '', a.agente_nome || '', a.area_nome || '',
      (a.quarteiroes_trabalhados || []).join(' '), a.lacre_etiqueta || '', a.quarteirao || '',
      a.especie_encontrada || '', a.larvas ?? '', a.pupas ?? '', a.exuvias ?? '', a.adultos ?? '',
      a.data_resultado || '', a.status, a.status === 'pendente' ? (a.dias_aguardando ?? '') : '', a.congelada_em || '',
    ]);
    const csv = [headersCsv, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(';')).join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `amostras_lab_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const cicloNomeAtual = ciclos.find(c => String(c.id) === cicloId)?.nome || '—';

  return (
    <div style={{ minHeight: '100vh', position: 'relative', background: C.bg }}>
      <style>{ANIMATIONS_CSS}</style>
      <Background />
      <div style={{ position: 'relative', zIndex: 1 }}>
        <PageHeader onVoltar={onVoltar} estatisticas={estatisticas} cicloNome={cicloNomeAtual} amostras={amostras} />
        <main style={{ maxWidth: 1920, margin: '0 auto', padding: '22px 28px 40px' }}>
          <FilterBar
            ciclos={ciclos} areas={areas}
            cicloId={cicloId} setCicloId={setCicloId}
            areaId={areaId} setAreaId={setAreaId}
            status={status} setStatus={setStatus}
            positivas={positivas} setPositivas={setPositivas}
            dataInicio={dataInicio} setDataInicio={setDataInicio}
            dataFim={dataFim} setDataFim={setDataFim}
            onExportar={exportarCSV}
          />

          {pendenciasResumo && pendenciasResumo.total_orfaos > 0 && (
            <div style={{
              margin: '12px 0',
              padding: '12px 16px',
              borderRadius: 8,
              border: `1px solid ${C.amber}55`,
              background: `${C.amber}0d`,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 16,
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                  <span style={{ fontSize: 14 }}>⚠️</span>
                  <span style={{ color: C.amber, fontWeight: 700, fontSize: 13, fontFamily: "'Inter', sans-serif" }}>
                    Amostras órfãs detectadas
                  </span>
                  <span style={{
                    background: `${C.amber}22`, border: `1px solid ${C.amber}55`,
                    color: C.amber, borderRadius: 10, padding: '1px 8px',
                    fontSize: 11, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace",
                  }}>
                    {pendenciasResumo.total_orfaos}
                  </span>
                </div>
                <div style={{ color: C.grayLt, fontSize: 12, fontFamily: "'Inter', sans-serif", lineHeight: 1.5 }}>
                  {pendenciasResumo.total_orfaos} FAD-01(s) com amostras coletadas sem registro no laboratório.{' '}
                  {pendenciasResumo.orfaos.slice(0, 3).map((o: any, i: number) => (
                    <span key={o.fad01_id}>
                      {i > 0 && ' · '}
                      <strong style={{ color: C.white }}>{o.area}</strong>
                      {' '}({o.data?.split('-').reverse().join('/')}, {o.agente}: {o.amostras_coletadas} amostra{o.amostras_coletadas > 1 ? 's' : ''})
                    </span>
                  ))}
                  {pendenciasResumo.orfaos.length > 3 && ` …e mais ${pendenciasResumo.orfaos.length - 3}`}
                </div>
              </div>
              <button
                onClick={sincronizarOrfaos}
                style={{
                  flexShrink: 0,
                  padding: '7px 14px',
                  borderRadius: 6,
                  border: `1px solid ${C.amber}66`,
                  background: `${C.amber}22`,
                  color: C.amber,
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: 'pointer',
                  fontFamily: "'Inter', sans-serif",
                  whiteSpace: 'nowrap',
                }}
              >
                Sincronizar agora
              </button>
            </div>
          )}

          <SamplesTable amostras={amostras} loading={loading} onEditar={setEditando} onAbrirFAD01={onAbrirFAD01} onCongelar={handleCongelar} alterandoId={alterandoId} />
          <div style={{ marginTop: 18 }}>
            <PositivesPanel positivos={positivos} cicloNome={cicloNomeAtual} />
          </div>
        </main>
        <div style={{ textAlign: 'center', padding: '24px 40px', borderTop: `1px solid ${C.border}`, marginTop: 24 }}>
          <span style={{ fontSize: 11, color: C.gray, fontFamily: "'Inter', sans-serif" }}>
            © 2026 Tovis — Desenvolvido por <span style={{ color: C.cyan, fontWeight: 500 }}>Walber Reis</span>
          </span>
        </div>
      </div>

      <LancarModal amostra={editando} onFechar={() => setEditando(null)} onSalvar={handleSalvar} />
    </div>
  );
};

export default Laboratorio;
